import React, { memo, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import BuildNumberInfo from './BuildNumberInfo';
import { numberFormatter } from '../../../utils/numbers';
import CustomPieChart from '../../data-widgets/CustomPieChart';
import { stringToColor } from '../../../utils/avatarStyles';
import StatsMissionSet, {
  filterRunReports,
  POSITIVE_CLEANING_MODES,
  RunReportFilterType,
} from '../../../models/service/stats/mission-set';

const SECONDS_TO_HOURS = 3600;

const BuildNumberColumn: React.FC<{
  buildSet: StatsMissionSet;
  multiple: boolean;
  repeatedDeploymentIds: string[] | null;
}> = ({ buildSet, multiple, repeatedDeploymentIds }) => {
  const { t } = useTranslation();

  const totalArea = useMemo(
    () =>
      buildSet.data.reduce(
        (acc, rr) =>
          (acc += filterRunReports(rr.modes, RunReportFilterType.All).reduce(
            (mAcc, m) => (mAcc += m.area),
            0,
          )),
        0,
      ),
    [buildSet.data],
  );

  const otherArea = useMemo(
    () =>
      buildSet.data.reduce(
        (acc, rr) =>
          (acc += filterRunReports(rr.modes, RunReportFilterType.OtherTypes).reduce(
            (mAcc, m) => (mAcc += m.area),
            0,
          )),
        0,
      ),
    [buildSet.data],
  );

  const totalHours = useMemo(
    () =>
      buildSet.data.reduce(
        (acc, rr) =>
          (acc += filterRunReports(rr.modes, RunReportFilterType.All).reduce(
            (mAcc, m) => (mAcc += m.duration),
            0,
          )),
        0,
      ) / SECONDS_TO_HOURS,
    [buildSet.data],
  );

  const otherHours = useMemo(
    () =>
      buildSet.data.reduce(
        (acc, rr) =>
          (acc += filterRunReports(rr.modes, RunReportFilterType.OtherTypes).reduce(
            (mAcc, m) => (mAcc += m.duration),
            0,
          )),
        0,
      ) / SECONDS_TO_HOURS,
    [buildSet.data],
  );

  return (
    <React.Fragment>
      <Grid container spacing={2}>
        <BuildNumberInfo
          buildSet={buildSet}
          multiple={multiple}
          repeatedDeploymentIds={repeatedDeploymentIds}
        />

        {buildSet.data.length > 0 && (
          <React.Fragment>
            <CustomPieChart
              fullWidth={multiple}
              label={t('chart-label-cleaning-performance')}
              mainCount={numberFormatter(totalArea / totalHours, 2)}
              data={[
                ...POSITIVE_CLEANING_MODES.map((cleaningMode) => ({
                  id: cleaningMode,
                  color: stringToColor(t('rsw-cleaning-mode-' + cleaningMode)),
                  label: cleaningMode,
                  value:
                    buildSet.data.reduce(
                      (acc, rr) =>
                        (acc += rr.modes
                          .filter((rm) => rm.code === cleaningMode)
                          .reduce((rAcc, r) => (rAcc += r.area), 0)),
                      0,
                    ) /
                    (buildSet.data.reduce(
                      (acc, rr) =>
                        (acc += rr.modes
                          .filter((rm) => rm.code === cleaningMode)
                          .reduce((rAcc, r) => (rAcc += r.duration), 0)),
                      0,
                    ) /
                      SECONDS_TO_HOURS),
                })),
                {
                  id: 'other',
                  value: otherArea / otherHours,
                  color: 'grey',
                  label: t('chart-label-other'),
                },
              ].sort((a, b) => b.value - a.value)}
            />
            <CustomPieChart
              fullWidth={multiple}
              label={t('chart-label-errors-per-area')}
              mainCount={(
                (buildSet.data.reduce(
                  (acc, bs) => (acc += bs.errors.reduce((eAcc, e) => (eAcc += e.count), 0)),
                  0,
                ) /
                  totalArea) *
                1000
              ).toFixed(2)}
              data={Array.from(
                buildSet.data.reduce(
                  (acc, bs) =>
                    new Set([
                      ...acc,
                      ...bs.errors.reduce((eAcc, e) => eAcc.add(e.code), new Set<string>()),
                    ]),
                  new Set<string>(),
                ),
              )
                .sort(
                  (a, b) =>
                    buildSet.data.reduce(
                      (acc, bs) =>
                        (acc += bs.errors
                          .filter((e) => e.code === b)
                          .reduce((eAcc, e) => (eAcc += e.count), 0)),
                      0,
                    ) -
                    buildSet.data.reduce(
                      (acc, bs) =>
                        (acc += bs.errors
                          .filter((e) => e.code === a)
                          .reduce((eAcc, e) => (eAcc += e.count), 0)),
                      0,
                    ),
                )
                .map((errorType) => ({
                  id: errorType ?? t('unknown-value'),
                  color: stringToColor(t('rsw-error-' + errorType)),
                  label: errorType,
                  value:
                    (buildSet.data.reduce(
                      (acc, bs) =>
                        (acc += bs.errors
                          .filter((e) => e.code === errorType)
                          .reduce((eAcc, e) => (eAcc += e.count), 0)),
                      0,
                    ) /
                      totalArea) *
                    1000,
                }))}
            />
            <CustomPieChart
              fullWidth={multiple}
              label={t('chart-label-maintenance-per-area')}
              mainCount={(
                (buildSet.data.reduce(
                  (acc, bs) => (acc += bs.maintenances.reduce((eAcc, e) => (eAcc += e.count), 0)),
                  0,
                ) /
                  totalArea) *
                1000
              ).toFixed(2)}
              data={Array.from(
                buildSet.data.reduce(
                  (acc, bs) =>
                    new Set([
                      ...acc,
                      ...bs.maintenances.reduce((eAcc, e) => eAcc.add(e.code), new Set<string>()),
                    ]),
                  new Set<string>(),
                ),
              )
                .sort(
                  (a, b) =>
                    buildSet.data.reduce(
                      (acc, bs) =>
                        (acc += bs.maintenances
                          .filter((e) => e.code === b)
                          .reduce((eAcc, e) => (eAcc += e.count), 0)),
                      0,
                    ) -
                    buildSet.data.reduce(
                      (acc, bs) =>
                        (acc += bs.maintenances
                          .filter((e) => e.code === a)
                          .reduce((eAcc, e) => (eAcc += e.count), 0)),
                      0,
                    ),
                )
                .map((errorType) => ({
                  id: errorType ?? t('unknown-value'),
                  color: stringToColor(t('rsw-error-' + errorType)),
                  label: errorType,
                  value:
                    (buildSet.data.reduce(
                      (acc, bs) =>
                        (acc += bs.maintenances
                          .filter((e) => e.code === errorType)
                          .reduce((eAcc, e) => (eAcc += e.count), 0)),
                      0,
                    ) /
                      totalArea) *
                    1000,
                }))}
            />
            <CustomPieChart
              fullWidth={multiple}
              label={t('chart-label-cleaned-area')}
              data={[
                ...POSITIVE_CLEANING_MODES.map((cleaningMode) => ({
                  id: cleaningMode,
                  color: stringToColor(t('rsw-cleaning-mode-' + cleaningMode)),
                  label: cleaningMode,
                  value: buildSet.data.reduce(
                    (acc, rr) =>
                      (acc += rr.modes
                        .filter((rm) => rm.code === cleaningMode)
                        .reduce((rAcc, r) => (rAcc += r.area), 0)),
                    0,
                  ),
                })),
                { id: 'other', value: otherArea, color: 'grey', label: t('chart-label-other') },
              ].sort((a, b) => b.value - a.value)}
            />
            <CustomPieChart
              fullWidth={multiple}
              label={t('chart-label-time-cleaned')}
              data={[
                ...POSITIVE_CLEANING_MODES.map((cleaningMode) => ({
                  id: cleaningMode,
                  color: stringToColor(t('rsw-cleaning-mode-' + cleaningMode)),
                  label: cleaningMode,
                  value:
                    buildSet.data.reduce(
                      (acc, rr) =>
                        (acc += rr.modes
                          .filter((rm) => rm.code === cleaningMode)
                          .reduce((rAcc, r) => (rAcc += r.duration), 0)),
                      0,
                    ) / SECONDS_TO_HOURS,
                })),
                { id: 'other', value: otherHours, color: 'grey', label: t('chart-label-other') },
              ].sort((a, b) => b.value - a.value)}
            />
          </React.Fragment>
        )}
      </Grid>
    </React.Fragment>
  );
};

export default memo(BuildNumberColumn);
